import "./FormField.scss"

import { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import cx from "classnames"

import { Flex } from "components/Flex"
import HelpButton, { IHelpButtonProps } from "components/HelpButton/HelpButton"
import Text from "components/Text/Text"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"

interface IFormFieldProps {
    id?: string
    label?: string
    labelTestId?: string
    badge?: string
    optional?: boolean
    note?: ReactNode
    noteAlign?: "right" | "split"
    className?: string
    helpTooltip?: IHelpButtonProps["tooltipProps"]
    isText?: boolean
    direction?: "horizontal" | "vertical"
    after?: ReactNode
    labelAfter?: ReactNode
}

const FormField: FC<React.PropsWithChildren<IFormFieldProps>> = ({
    id,
    label,
    labelTestId,
    note,
    noteAlign,
    optional,
    badge,
    className = "",
    helpTooltip,
    isText,
    direction = "vertical",
    children,
    after,
    labelAfter
}) => {
    const { t } = useTranslation()

    return (
        <div className={cx([
                "form-field",
                `form-field--direction-${direction}`,
                className,
            ])}>
            <Flex className="form-field__label-container" alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    {!!label &&
                        <label htmlFor={id} className="form-field__label" data-testid={labelTestId}>
                            <FlexibleContent text={label} />
                        </label>
                    }
                    {!!labelAfter &&
                        labelAfter
                    }
                    {!!helpTooltip &&
                        <HelpButton tooltipProps={helpTooltip} />
                    }
                </Flex>
                {!!after &&
                    after
                }
            </Flex>
            <div className={isText ? 'mt-1' : ''}>
                {children}
            </div>
            {!!(note || optional || badge) &&
                <div className={cx(["form-field__bottom", { "justify-content-end": noteAlign === "right" }])}>
                    {noteAlign === "split"
                        ? <>
                            <Text type="pSmallest" className="color-grey-500">
                                {optional && `(${t('common.optional')})`}
                            </Text>
                            {note &&
                                <Text type="pSmallest" className="color-grey-500">{note}</Text>
                            }
                        </>
                        : <Text type="pSmallest" className="color-grey-500">
                            {optional && `(${t('common.optional')})`}  {note}
                        </Text>
                    }
                    {badge &&
                        <div className="form-field__badge">{badge}</div>
                    }
                </div>
            }
        </div>
    )
}

export default FormField
